import { IdentityPrincipal } from './identity-principal';
import { Role } from './role';

/// <code-import> Place custom imports between <code-import> tags
import { EntityName } from '@mri-platform/dsg/core';
import { AuthzContext, AuthzContextsActionMap, CrudAction } from '@mri-platform/resource-authz';
import { DataProperty, DataType, MetadataStore } from 'breeze-client';
import attempt from 'lodash-es/attempt';
import isString from 'lodash-es/isString';
import { BaseEntity } from '../base-entity';
/// </code-import>

export interface OrganisationPrincipal {
  defaultRoleId?: string;
  domainNames: string;
  isLinkedToAgoraGatewayAppTile: boolean;
  defaultRole: Role;
}

/// <module-code> Place module level code between <module-code> tags

export function normalizeDomainNames(value: unknown): string[] {
  const domains = Array.isArray(value) ? value : parseJsonArray(value);
  return domains.filter(isString);
}

function parseJsonArray(value: unknown): unknown[] {
  if (!isString(value)) return [];
  const parsed = attempt(JSON.parse, value);
  return Array.isArray(parsed) ? parsed : [];
}

export enum OrganisationPrincipalCustomAction {
  Impersonate = 'Impersonate',
  RecoverDelete = 'RecoverDelete'
}

export type OrganisationName = Pick<OrganisationPrincipal, 'name'>;

const typeFuncs = BaseEntity.makeStaticTypeFunctions<OrganisationPrincipal>(EntityName.Organisation);
/// </module-code>

export class OrganisationPrincipal extends IdentityPrincipal {
  /// <code> Place custom code between <code> tags
  static propName = typeFuncs.propName;
  static propNames = typeFuncs.propNames;
  static qualifiedPropName = typeFuncs.qualifiedPropName;
  static qualifiedPropNames = typeFuncs.qualifiedPropNames;

  get domainNamesText(): string {
    return normalizeDomainNames(this.domainNames).join(', ');
  }

  set domainNamesText(value: string) {
    const next = value
      .split(',')
      .map(domain => domain.trim())
      .filter(Boolean);
    if (next.join() === normalizeDomainNames(this.domainNames).join()) return;
    this.domainNames = next as unknown as string;
  }

  static override register(metadataStore: MetadataStore) {
    const entityType = metadataStore.getAsEntityType(EntityName.Organisation, false);
    (entityType.getProperty(OrganisationPrincipal.propName('domainNames'), true) as DataProperty).dataType =
      DataType.Undefined;
  }

  static authorization: AuthzContextsActionMap = {
    ...AuthzContextsActionMap.crudFor(EntityName.Organisation),
    [OrganisationPrincipalCustomAction.Impersonate]: {
      action: OrganisationPrincipalCustomAction.Impersonate,
      resource: EntityName.Organisation
    },
    [OrganisationPrincipalCustomAction.RecoverDelete]: {
      action: OrganisationPrincipalCustomAction.RecoverDelete,
      resource: EntityName.Organisation
    },
    [CrudAction.Update]: AuthzContext.updateFor(...OrganisationPrincipal.qualifiedPropNames('name', 'domainNames')),
    [CrudAction.Delete]: AuthzContext.updateFor(OrganisationPrincipal.qualifiedPropName('isDeleted'))
  };
  /// </code>
  // Generated code. Do not place code below this line.
}
